import {
  BarChartAnnotation,
  BarChartData,
  BarChartLayout,
  BarChartLegend,
  BarChartShape,
} from "Components/Chart/BarChart/barChartInterface";
import {
  CoverageNeed,
  ProductCoverageName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  isAkaGroupLifeTimeSelected,
  isDisabilityInsuranceSelected,
} from "Store/akaAdvice/mappers/mapToCartProducts/mapToCartProducts";

import { getChartSize } from "Components/Chart/chartConfig";
import { VALUE_TYPE } from "Components/NumberValue";
import { getPriceFormatFromValue } from "Formatting/index";
import { getPayment } from "Store/akaAdvice/interfaces/getters";
import { WindowSize } from "Utils/screenSizeUtils";
import { FormikProps } from "formik";
import { AkaAdvicePriceFormValues } from "../../interfaces";

export const getDisabilityMonthlyChartData = (
  formProps: FormikProps<AkaAdvicePriceFormValues>,
  need: CoverageNeed,
  baseAmount: number,
): BarChartData[] => {
  const effectivePaymentRateAkaGroupLifeTime = getPayment(
    need,
    ProductCoverageName.AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST,
  ).effectivePaymentRate;
  const effectivePaymentRateDisabilityFlex = getPayment(
    need,
    ProductCoverageName.DISABILITY_FLEX_PENSION,
  ).effectivePaymentRate;

  const { timeLimitedDisabilityInterestGLevel, disabilityInsuredSumSlider } =
    formProps.values;
  const navPayout = need.existingCoverages.NAV_PAYOUT;
  const employerPayout = need.existingCoverages.EMPLOYER_PAYOUT;
  const xAxisCoordinates = ["år 1-4", "fra år 4"];

  const disabilityFlexAdviceValue = isDisabilityInsuranceSelected(
    formProps.values,
  )
    ? (disabilityInsuredSumSlider ?? 0) * effectivePaymentRateDisabilityFlex
    : 0;
  const adviceValueTimeLimited = isAkaGroupLifeTimeSelected(formProps.values)
    ? Number(timeLimitedDisabilityInterestGLevel) *
      baseAmount *
      effectivePaymentRateAkaGroupLifeTime
    : 0;

  const barChartData: BarChartData[] = [];

  if (navPayout > 0) {
    barChartData.push({
      x: xAxisCoordinates,
      y: Array(2).fill(getMonthly(navPayout)),
      width: 0.4,
      hovertemplate: Array(2).fill(
        getPriceFormatFromValue(getMonthly(navPayout), VALUE_TYPE.NOK),
      ),
      name: "NAV uføretrygd",
      marker: {
        color: "#957851",
      },
    });
  }
  if (employerPayout > 0) {
    barChartData.push({
      x: xAxisCoordinates,
      y: Array(2).fill(getMonthly(employerPayout)),
      width: 0.4,
      hovertemplate: Array(2).fill(
        getPriceFormatFromValue(getMonthly(employerPayout), VALUE_TYPE.NOK),
      ),
      name: "Arbeidsgivers forsikring",
      marker: {
        color: "#F5E4C7",
      },
    });
  }
  if (disabilityFlexAdviceValue > 0) {
    barChartData.push({
      x: xAxisCoordinates,
      y: Array(2).fill(getMonthly(disabilityFlexAdviceValue)),
      width: 0.4,
      hovertemplate: Array(2).fill(
        getPriceFormatFromValue(
          getMonthly(disabilityFlexAdviceValue),
          VALUE_TYPE.NOK,
        ),
      ),
      name: "Individuell uføreforsikring (reguleres ikke i utbetalingsperioden)",
      marker: {
        color: "#003F4A",
      },
    });
  }
  if (adviceValueTimeLimited > 0) {
    const adviceValueTimeLimitedFormatted = getPriceFormatFromValue(
      getMonthly(adviceValueTimeLimited),
      VALUE_TYPE.NOK,
    );
    barChartData.push({
      x: xAxisCoordinates,
      y: [getMonthly(adviceValueTimeLimited), 0],
      width: 0.4,
      hovertemplate: [adviceValueTimeLimitedFormatted, ""],
      name: "A+ Gruppeliv begrenset uførerente",
      marker: {
        color: "#7ACDDC",
      },
    });
  }

  return barChartData;
};

export const getDisabilityMonthlyChartLayout = (
  windowSize: WindowSize,
  need: CoverageNeed,
  netIncome: number,
): BarChartLayout => {
  const monthlyNetIncome = getMonthly(netIncome);
  const navPayout = need.existingCoverages.NAV_PAYOUT;
  const employerPayout = need.existingCoverages.EMPLOYER_PAYOUT;
  const totalPayout = navPayout + employerPayout;
  const totalMonthylPayout = getMonthly(totalPayout);
  return {
    title: "",
    size: getChartSize(windowSize, 500),
    xAxis: { type: "category" },
    yAxis: {
      showline: true,
      showgrid: false,
      nticks: 2,
      tickvals: [monthlyNetIncome, totalMonthylPayout],
      ticktext: [
        getPriceFormatFromValue(monthlyNetIncome, VALUE_TYPE.NOK),
        getPriceFormatFromValue(totalMonthylPayout, VALUE_TYPE.NOK),
      ],
    },
    legend,
    shapes: getShapes(monthlyNetIncome, totalMonthylPayout),
    annotations: getAnnotations(monthlyNetIncome, totalMonthylPayout),
  };
};

const legend: BarChartLegend = {
  orientation: "h",
  y: -0.1,
};

const getShapes = (
  monthlyNetIncome: number,
  totalMonthlyPayout: number,
): BarChartShape[] => [
  {
    type: "line",
    xref: "paper",
    yref: "y",
    x: { start: 0, end: 0.7 },
    y: { start: monthlyNetIncome, end: monthlyNetIncome },
    line: {
      width: 2,
      dash: "dot",
    },
  },
  {
    type: "line",
    xref: "paper",
    yref: "y",
    x: { start: 0, end: 0.7 },
    y: { start: totalMonthlyPayout, end: totalMonthlyPayout },
    line: {
      width: 2,
      dash: "dot",
    },
  },
];

const getAnnotations = (
  monthlyNetIncome: number,
  totalMonthlyPayout: number,
): BarChartAnnotation[] => [
  {
    text: "Dagens inntekt",
    x: 2,
    y: monthlyNetIncome,
    showarrow: false,
    xshift: 0,
    yshift: 0,
  },
  {
    text: "Inntekt uten<br>ekstra forsikring",
    x: 2,
    y: totalMonthlyPayout,
    showarrow: false,
    xshift: 0,
    yshift: 0,
  },
];

export const getMonthly = (value: number): number => Math.trunc(value / 12);
